import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Button,
  ButtonProps,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Popper,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { TrackerDataType } from '../../shared/constants/constants';
import { DownloadCSVButtonUIColor } from '../../shared/UIComponents/DownloadCSVButtonUI';
import {
  checkDetailedCsvExport,
  DetailedCsvExportContext,
  downloadDetailedCsvExport,
} from '../../shared/requests/detailedCsvExportRequests';

type DetailedCsvModalState = 'closed' | 'checking' | 'allowed' | 'blocked' | 'error';
type ButtonSxOverride = boolean | Exclude<NonNullable<SxProps<Theme>>, readonly unknown[]>;

const detailedCsvIncludedFieldsByTrackerType: Partial<Record<TrackerDataType, string>> = {
  [TrackerDataType.MEDIA_INTERACTION]: 'Key Messaging and Background/Response',
  [TrackerDataType.SERVICE]: 'Key Messaging and Background/Response',
  [TrackerDataType.ENGAGEMENT]: 'Notes/Details and Background Information',
  [TrackerDataType.CONTENT_LOG]: 'Content and Background Information',
};

interface TrackerTableDownloadButtonProps {
  rows: any[];
  dropdowns: any;
  flattenerCallback: (data: any, dropdownTable: any) => any[];
  filename: string;
  color: DownloadCSVButtonUIColor;
  variant?: ButtonProps['variant'];
  buttonSx?: SxProps<Theme>;
  detailedExportContext?: DetailedCsvExportContext;
}

export default function TrackerTableDownloadButton(props: Readonly<TrackerTableDownloadButtonProps>) {
  const {
    rows,
    dropdowns,
    flattenerCallback,
    filename,
    color,
    variant = 'contained',
    buttonSx,
    detailedExportContext,
  } = props;

  const [csvData, setCsvData] = useState<any[]>([]);
  const [pendingSummaryDownload, setPendingSummaryDownload] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [modalState, setModalState] = useState<DetailedCsvModalState>('closed');
  const [openDetailedDialog, setOpenDetailedDialog] = useState(false);
  const [isDownloadingDetailed, setIsDownloadingDetailed] = useState(false);
  const csvLink = useRef<any>(null);
  const detailedPrecheckRequestId = useRef(0);

  useEffect(() => {
    if (!pendingSummaryDownload) {
      return;
    }

    if (csvLink.current?.link) {
      csvLink.current.link.click();
    }
    setPendingSummaryDownload(false);
  }, [csvData, pendingSummaryDownload]);

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleSummaryDownload = () => {
    closeMenu();
    const formattedRows = flattenerCallback(rows, dropdowns);
    setCsvData(formattedRows);
    setPendingSummaryDownload(true);
  };

  const openDetailedExportDialog = async () => {
    if (!detailedExportContext) {
      return;
    }

    closeMenu();
    const requestId = detailedPrecheckRequestId.current + 1;
    detailedPrecheckRequestId.current = requestId;
    setOpenDetailedDialog(true);
    setModalState('checking');

    try {
      const response = await checkDetailedCsvExport(detailedExportContext);
      if (detailedPrecheckRequestId.current !== requestId) {
        return;
      }

      setModalState(response.allowed ? 'allowed' : 'blocked');
    } catch {
      if (detailedPrecheckRequestId.current !== requestId) {
        return;
      }

      setModalState('error');
    }
  };

  const closeDetailedDialog = () => {
    detailedPrecheckRequestId.current += 1;
    setOpenDetailedDialog(false);
    setIsDownloadingDetailed(false);
  };

  const resetDetailedDialogState = () => {
    setModalState('closed');
    setIsDownloadingDetailed(false);
  };

  const handleDetailedDownload = async () => {
    if (!detailedExportContext || modalState !== 'allowed') {
      return;
    }

    setIsDownloadingDetailed(true);

    try {
      await downloadDetailedCsvExport(detailedExportContext);
      closeDetailedDialog();
    } catch {
      setIsDownloadingDetailed(false);
      setModalState('error');
    }
  };

  const renderDetailedDialogContent = () => {
    if (modalState === 'checking') {
      return (
        <>
          <CircularProgress size={20} />
          <Typography sx={{ marginTop: '1rem' }}>Checking detailed export size...</Typography>
        </>
      );
    }

    if (modalState === 'allowed') {
      const includedFields = detailedExportContext
        ? detailedCsvIncludedFieldsByTrackerType[detailedExportContext.trackerType]
        : undefined;

      return <Typography>Detailed CSV is ready. This export includes {includedFields}.</Typography>;
    }

    if (modalState === 'blocked') {
      return <Typography>6MB size limit exceeded, narrow by filter/search or switch to Download Summary</Typography>;
    }

    return <Typography>Unable to prepare detailed CSV export right now.</Typography>;
  };

  const menuOpen = Boolean(anchorEl);
  let buttonSxOverrides: ButtonSxOverride[] = [];
  if (Array.isArray(buttonSx)) {
    buttonSxOverrides = [...buttonSx] as ButtonSxOverride[];
  } else if (buttonSx) {
    buttonSxOverrides = [buttonSx as ButtonSxOverride];
  }
  const mergedButtonSx: SxProps<Theme> = [{ height: '32px' }, ...buttonSxOverrides];

  return (
    <>
      <Button
        sx={mergedButtonSx}
        variant={variant}
        color={color}
        startIcon={<SaveAltIcon />}
        onClick={(event) => setAnchorEl(menuOpen ? null : event.currentTarget)}
        data-cy="downloadCsvButton"
      >
        Download
      </Button>
      <Popper
        open={menuOpen}
        anchorEl={anchorEl}
        placement="bottom-end"
        disablePortal={false}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              padding: 12,
            },
          },
        ]}
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={closeMenu}>
          <Paper
            data-cy="downloadCsvMenu"
            elevation={8}
            sx={{
              minWidth: 200,
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <MenuItem onClick={handleSummaryDownload} data-cy="downloadSummaryCsvButton">
              Download Summary
            </MenuItem>
            {detailedExportContext && (
              <MenuItem onClick={openDetailedExportDialog} data-cy="downloadDetailedCsvButton">
                Download Detailed
              </MenuItem>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
      <CSVLink
        style={{ display: 'none' }}
        filename={Date.now() + '-' + filename + '.csv'}
        data={csvData}
        ref={csvLink}
        separator=","
      />
      {detailedExportContext && (
        <Dialog
          open={openDetailedDialog}
          onClose={closeDetailedDialog}
          fullWidth
          maxWidth="sm"
          data-cy="detailedCsvExportDialog"
          TransitionProps={{ onExited: resetDetailedDialogState }}
        >
          <DialogTitle>Download Detailed</DialogTitle>
          <DialogContent sx={{ minHeight: 88, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {renderDetailedDialogContent()}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDetailedDialog}>Cancel</Button>
            {modalState === 'allowed' && (
              <Button
                onClick={handleDetailedDownload}
                variant="contained"
                color={color}
                disabled={isDownloadingDetailed}
                data-cy="confirmDetailedCsvDownloadButton"
              >
                {isDownloadingDetailed ? 'Downloading...' : 'Download'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
